<template>
  <!--
    Was reachable any time (a persistent free-dial keypad + "Enter number"
    field, always visible, calling straight into the WebRTC SIP layer with
    NO task_id/request_id ever registered) -- confirmed live 2026-08-05 that
    agents were placing real campaign calls through this instead of the
    per-lead call dialog (CampaignDetailPage.vue's placeSipCall, which
    correctly calls initiateSoftphoneCall({task_id, request_id}) before
    dialing), leaving those calls permanently untracked and invisible on
    the campaign dashboard. Now only ever renders once a call is already
    active (started the tracked way) -- in-call controls only, no way to
    originate a new untracked call from here.
  -->
  <div
    v-if="inCall"
    class="sp-dialer"
    :class="{
      'sp-dialer--open': open,
      'sp-dialer--incall': inCall,
      'sp-dialer--muted': muted,
    }"
  >
    <button
      v-if="!open"
      type="button"
      class="sp-dialer__fab"
      :title="fabTitle"
      :aria-label="fabTitle"
      @click="open = true"
    >
      <span class="sp-dialer__fab-ring" aria-hidden="true" />
      <v-icon icon="mdi-phone-in-talk" size="22" />
      <span class="sp-dialer__fab-dot" :class="statusDotClass" aria-hidden="true" />
    </button>

    <div v-else class="sp-dialer__panel" role="dialog" aria-label="Softphone call controls">
      <div class="sp-dialer__glow" aria-hidden="true" />

      <header class="sp-dialer__header">
        <div class="sp-dialer__header-text">
          <span class="sp-dialer__eyebrow">Softphone</span>
          <span class="sp-dialer__title">On call</span>
        </div>
        <div class="sp-dialer__header-actions">
          <span class="sp-dialer__pill" :class="statusDotClass">
            <span class="sp-dialer__pill-dot" aria-hidden="true" />
            {{ statusLabel }}
          </span>
          <button
            type="button"
            class="sp-dialer__icon-btn"
            aria-label="Minimize"
            @click="open = false"
          >
            <v-icon icon="mdi-close" size="18" />
          </button>
        </div>
      </header>

      <div class="sp-dialer__stage">
        <div class="sp-dialer__pulse" :class="{ 'is-ringing': phase === 'ringing' }" aria-hidden="true">
          <div class="sp-dialer__pulse-core">
            <v-icon :icon="muted ? 'mdi-microphone-off' : 'mdi-phone-in-talk'" size="28" />
          </div>
        </div>
        <div class="sp-dialer__call-timer">
          {{ phase === 'ringing' ? 'Ringing…' : formatDuration(durationSec) }}
        </div>
        <p v-if="muted" class="sp-dialer__mute-hint">Microphone muted</p>
        <p v-if="error" class="sp-dialer__error">{{ error }}</p>
      </div>

      <!-- DTMF only now -- there's no number-entry mode left to press these into. -->
      <div class="sp-dialer__pad" role="group" aria-label="Keypad (send touch-tones)">
        <button
          v-for="key in keys"
          :key="key.digit"
          type="button"
          class="sp-dialer__key"
          @click="pressKey(key.digit)"
        >
          <span class="sp-dialer__key-digit">{{ key.digit }}</span>
          <span v-if="key.letters" class="sp-dialer__key-letters">{{ key.letters }}</span>
        </button>
      </div>

      <div class="sp-dialer__actions sp-dialer__actions--incall">
        <button
          type="button"
          class="sp-dialer__ctrl"
          :class="{ 'is-active': muted }"
          :aria-pressed="muted"
          :aria-label="muted ? 'Unmute microphone' : 'Mute microphone'"
          @click="toggleMute"
        >
          <v-icon :icon="muted ? 'mdi-microphone-off' : 'mdi-microphone'" size="22" />
          <span>{{ muted ? 'Unmute' : 'Mute' }}</span>
        </button>
        <button type="button" class="sp-dialer__hangup" @click="endCall">
          <v-icon icon="mdi-phone-hangup" size="20" />
          End
        </button>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue'
import {
  getSoftphoneStatus,
  hangupSoftphoneCall,
  sendSoftphoneDtmf,
  setSoftphoneMuted,
} from '@/services/softphone'
import '@/styles/softphone/dialer.css'

defineOptions({ name: 'SoftphoneDialer' })

const keys = [
  { digit: '1', letters: '' },
  { digit: '2', letters: 'ABC' },
  { digit: '3', letters: 'DEF' },
  { digit: '4', letters: 'GHI' },
  { digit: '5', letters: 'JKL' },
  { digit: '6', letters: 'MNO' },
  { digit: '7', letters: 'PQRS' },
  { digit: '8', letters: 'TUV' },
  { digit: '9', letters: 'WXYZ' },
  { digit: '*', letters: '' },
  { digit: '0', letters: '+' },
  { digit: '#', letters: '' },
]

const open = ref(false)
const error = ref('')
const registered = ref(false)
const starting = ref(false)
const inCall = ref(false)
const muted = ref(false)
const phase = ref('idle')
const durationSec = ref(0)

let pollTimer = null

function formatDuration(totalSec) {
  const sec = Math.max(0, Number(totalSec) || 0)
  const m = Math.floor(sec / 60)
  const s = sec % 60
  return `${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`
}

const statusLabel = computed(() => {
  if (phase.value === 'ringing') return 'Ringing'
  if (phase.value === 'connected' || inCall.value) return muted.value ? 'Muted' : 'On call'
  if (registered.value) return 'Ready'
  if (starting.value) return 'Connecting…'
  return 'Offline'
})

const statusDotClass = computed(() => {
  if (muted.value && inCall.value) return 'is-muted'
  if (phase.value === 'ringing') return 'is-connecting'
  if (phase.value === 'connected' || inCall.value) return 'is-call'
  if (registered.value) return 'is-ready'
  if (starting.value) return 'is-connecting'
  return 'is-offline'
})

const fabTitle = computed(() => `Softphone — ${statusLabel.value}`)

function refreshStatus() {
  const status = getSoftphoneStatus()
  registered.value = status.registered
  starting.value = status.starting
  inCall.value = status.inCall
  muted.value = Boolean(status.muted)
  phase.value = status.phase || (status.inCall ? 'connected' : 'idle')
  durationSec.value = status.durationSec || 0
  if (!status.inCall) muted.value = false
}

function pressKey(digit) {
  error.value = ''
  sendSoftphoneDtmf(digit)
}

function toggleMute() {
  if (!inCall.value) return
  const next = !muted.value
  const ok = setSoftphoneMuted(next)
  if (ok) muted.value = next
  else error.value = 'Could not change mute.'
  refreshStatus()
}

function endCall() {
  hangupSoftphoneCall()
  muted.value = false
  refreshStatus()
  error.value = ''
}

onMounted(() => {
  refreshStatus()
  pollTimer = window.setInterval(refreshStatus, 400)
})

onBeforeUnmount(() => {
  if (pollTimer) window.clearInterval(pollTimer)
})
</script>
